import {
    Alert,
    Box,
    Chip,
    Collapse,
    Icon,
    LinearProgress,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { AuctionCatalogueStatusEnum } from '../../../_common/enum/auctionCatalogueStatus.enum';
import { StandardPanel } from '../../../_components/panel/standard.panel';
import { handleErrorResponse } from '../../../_helpers/handleResponse.helper';
import { catalogueService } from '../../../_services/catalogueService.service';
import { AuctionPostbackStatusEnum } from '../../../_common/enum/auctionPostbackStatus.enum';
import { ActualAuctionStatusEnum } from '../../../_common/enum/actualAuctionStatus';
import { accountService } from '../../../_services/accounts.service';
import { MessageNotification } from '../../../_components/box/message-notification';
import { getMoneyFormat } from '../../../_helpers/format.helper';

function catalogueItemPrimaryLabel(catalogueItem: any): string {
    const base = `[#${catalogueItem.lotNo}] ${catalogueItem.product} - ${catalogueItem.district} - ${catalogueItem.totalVolume}`;
    if (!accountService.isAdmin) return base;
    const raw = catalogueItem.minBid;
    const reserve =
        raw != null && raw !== '' && !Number.isNaN(Number(raw))
            ? getMoneyFormat(Number(raw))
            : '—';
    return `${base} · Reserve: ${reserve}`;
}

export function AuctionCatalogueList(params: { auction: any, catalogueItem: any, actualAuctionStatusId: ActualAuctionStatusEnum, proxyBidderId?: string }) {

    const [data, setData] = useState<any>([]);

    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const [setupNavOpen, setSetupNavOpen] = useState(true);

    useEffect(() => {
        if (!!params.auction && !!params.auction.id)
            fetchData(params.auction.id);
    }, [params.auction?.id]);

    function fetchData(id: number) {
        setLoading(true);
        return catalogueService.getAllAuction(id)
            .then(
                data => {

                    if (!!data && !!data.length) {
                        data.forEach((catalogue: any) => {
                            catalogue.expanded = true;
                        });
                    }
                    setData(data);
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        if (!!params.catalogueItem && !!params.catalogueItem.auctionCatalogueStatusId && !!data && !!data.length) {
            data.forEach((c: any) => {
                let item = c.catalogueItems.find((m: any) => m.id == params.catalogueItem.id);
                if (!!item && item.auctionCatalogueStatusId != params.catalogueItem.auctionCatalogueStatusId) {
                    setData((_data: any) => {
                        let __data = [..._data];
                        let catalogue = __data.find((_c: any) => _c.id == c.id);
                        let catalogueItem = catalogue.catalogueItems.find((_ci: any) => _ci.id == params.catalogueItem.id);
                        catalogueItem.auctionCatalogueStatusId = params.catalogueItem.auctionCatalogueStatusId;
                        catalogueItem.auctionPostbackStatus = params.catalogueItem.auctionPostbackStatus;
                        return __data;
                    })
                }
            });
        }
    }, [params.catalogueItem?.auctionCatalogueStatusId, data.length]);

    function toggleExpanded(catalogue: any, index: number) {
        setData((_data: any) => {
            let __data = [..._data];
            __data[index].expanded = !__data[index].expanded;
            return __data;
        })
    }

    function isChildActive(catalogue: any) {
        if (!catalogue || !catalogue.catalogueItems) return false;
        return catalogue.catalogueItems.some((catalogueItem: any) =>
            catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Active ||
            catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.BidAcceptance ||
            catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.ReOffered ||
            catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.ClosingShortly
        )
    }

    const listPadding = { xs: 0.5, sm: 1 };
    const nestedIndent = { xs: '20px', sm: '26px' };
    const alertIndent = { xs: '20px', sm: '50px' };

    return (
        <StandardPanel height="600px" title={'Auction Catalogue'} loading={false}>
            {!!loading && <LinearProgress />}
            {(!loading && (!data || !data.length)) && <MessageNotification message='No catalogues associated with auction' />}
            {(!loading && !!data && !!data.length) && data.map((catalogue: any, index: number) => (
                <Box key={catalogue.id} sx={{ px: listPadding }}>
                    <ListItemButton
                        disabled={(!catalogue.catalogueItems || !catalogue.catalogueItems.length)}
                        sx={{
                            mt: 0,
                            pt: 0.5,
                            pb: 0.5,
                            px: { xs: 0.5, sm: 1 },
                            minHeight: { xs: 40, sm: 48 },
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, sm: 1 },
                        }}
                        onClick={() => toggleExpanded(catalogue, index)}
                    >
                        <ListItemIcon sx={{ minWidth: { xs: 32, sm: 40 } }}>
                            <Icon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>account_tree</Icon>
                        </ListItemIcon>
                        <ListItemText
                            primary={`${catalogue.compNo}-${catalogue.compType}-${catalogue.title}`}
                            sx={{ minWidth: 0 }}
                            primaryTypographyProps={{
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                noWrap: true,
                                sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                            }}
                        />
                        {(catalogue.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Active || isChildActive(catalogue)) ? <Chip sx={{ minWidth: { xs: 80, sm: 85 }, flexShrink: 0 }} size="small" label="Active" color="success" /> :
                            catalogue.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Completed ? <Chip sx={{ minWidth: { xs: 80, sm: 85 }, opacity: 0.4, pointerEvents: 'none', flexShrink: 0 }} size="small" label="Completed" color="default" /> :
                                <Chip size="small" label="Pending" sx={{ minWidth: { xs: 80, sm: 85 }, opacity: 0.4, pointerEvents: 'none', flexShrink: 0 }} color="default" />}
                        {(!catalogue.catalogueItems || !catalogue.catalogueItems.length) ? <Icon sx={{ fontSize: '1rem' }}>remove</Icon> : catalogue.expanded ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    {!!catalogue.catalogueItems && (
                        <Collapse in={catalogue.expanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{ py: 0 }}>
                                {catalogue.catalogueItems.map((catalogueItem: any) => (
                                    <Box key={catalogueItem.id}>
                                        <ListItemButton
                                            sx={{
                                                py: { xs: 0.25, sm: 0.5 },
                                                pl: { xs: 2.5, sm: 3 },
                                                pr: { xs: 0.5, sm: 1 },
                                                minHeight: { xs: 36, sm: 40 },
                                                marginLeft: nestedIndent,
                                            }}
                                            onClick={() => {}}
                                        >
                                            <ListItemIcon sx={{ minWidth: { xs: 28, sm: 36 } }}>
                                                <Icon fontSize="small" sx={{ color: 'text.disabled', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                    subdirectory_arrow_right
                                                </Icon>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={catalogueItemPrimaryLabel(catalogueItem)}
                                                sx={{ minWidth: 0 }}
                                                primaryTypographyProps={{
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    noWrap: true,
                                                    sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                                                }}
                                            />
                                            {(catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Active || catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.ClosingShortly) ? <Chip sx={{ minWidth: { xs: 80, sm: 85 }, flexShrink: 0 }} size="small" label="Active" color="success" /> :
                                                (catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.BidAcceptance) ? <Chip sx={{ minWidth: { xs: 80, sm: 85 }, flexShrink: 0 }} size="small" label="Evaluating" color="primary" /> :
                                                    (catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.ReOffered) ? <Chip sx={{ minWidth: { xs: 80, sm: 85 }, flexShrink: 0 }} size="small" label={accountService.isAdminOrMonitorOrAuctioneer ? "ReOffered" : "Tied"} color="primary" /> :
                                                        catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Completed ? <Chip sx={{ minWidth: { xs: 80, sm: 85 }, opacity: 0.4, pointerEvents: 'none', flexShrink: 0 }} size="small" label="Completed" color="default" /> :
                                                            catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Withdrawn ? <Chip sx={{ minWidth: { xs: 80, sm: 85 }, opacity: 0.4, pointerEvents: 'none', flexShrink: 0 }} size="small" label="Withdrawn" color="error" /> :
                                                                <Chip size="small" label="Pending" sx={{ minWidth: { xs: 80, sm: 85 }, opacity: 0.4, pointerEvents: 'none', flexShrink: 0 }} color="default" />}
                                        </ListItemButton>

                                        {(
                                            (!accountService.isAdminOrMonitorOrAuctioneer && !!catalogueItem?.auctionPostbackStatus && catalogueItem.auctionPostbackStatus.auctionPostbackStatusId === AuctionPostbackStatusEnum.Won && catalogueItem.auctionCatalogueStatusId !== AuctionCatalogueStatusEnum.Withdrawn) ||
                                            (accountService.isProxyBidder && params.proxyBidderId && catalogueItem.auctionCatalogueStatusId !== AuctionCatalogueStatusEnum.ReOffered && catalogueItem?.auctionPostbackStatus?.winnerUserIds.some((w: string) => w == params.proxyBidderId))
                                        ) && (
                                            <Alert
                                                severity="success"
                                                sx={{ py: 0.25, px: 1, marginLeft: alertIndent, '& .MuiAlert-message': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}
                                            >
                                                Congratulations! Bid won: €{catalogueItem.auctionPostbackStatus.bid?.toFixed(2)}
                                            </Alert>
                                        )}

                                        {(!accountService.isAdminOrMonitorOrAuctioneer && !!catalogueItem?.auctionPostbackStatus && catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Withdrawn) && (
                                            <Alert severity="error" sx={{ py: 0.25, px: 1, marginLeft: alertIndent, '& .MuiAlert-message': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
                                                Withdrawn
                                            </Alert>
                                        )}

                                        {(
                                            (!accountService.isAdminOrMonitorOrAuctioneer && !!catalogueItem?.auctionPostbackStatus && catalogueItem.auctionPostbackStatus.auctionPostbackStatusId === AuctionPostbackStatusEnum.Lost && (catalogueItem.auctionCatalogueStatusId !== AuctionCatalogueStatusEnum.Withdrawn && catalogueItem.auctionCatalogueStatusId !== AuctionCatalogueStatusEnum.ReOffered)) &&
                                            !(accountService.isProxyBidder && params.proxyBidderId && catalogueItem.auctionCatalogueStatusId !== AuctionCatalogueStatusEnum.ReOffered && catalogueItem?.auctionPostbackStatus?.winnerUserIds.some((w: string) => w == params.proxyBidderId))
                                        ) && (
                                            <Alert severity="info" sx={{ py: 0.25, px: 1, marginLeft: alertIndent, '& .MuiAlert-message': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
                                                Lot sold for: €{catalogueItem.auctionPostbackStatus.bid?.toFixed(2)}
                                            </Alert>
                                        )}

                                        {(!!accountService.isAdminOrMonitorOrAuctioneer && !!catalogueItem?.auctionPostbackStatus && (catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Completed || catalogueItem.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.Withdrawn)) && (
                                            <Alert severity="info" sx={{ py: 0.25, px: 1, marginLeft: alertIndent, '& .MuiAlert-message': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
                                                Final bid: €{catalogueItem.auctionPostbackStatus.bid?.toFixed(2)}
                                            </Alert>
                                        )}
                                    </Box>
                                ))}
                            </List>
                        </Collapse>
                    )}
                </Box>
            ))}
        </StandardPanel>
    );
}
